import { ElementTypeTransitionCommand } from '../commands/ElementTypeTransitionCommand';
import { UpdateElementCommand } from '../commands/UpdateElementCommand';
import { EditorChangeType } from '../types';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export class UpdateMultipleElementsCommand implements Command {
  private editor: VideoEditor;
  private readonly _shouldCreateUndoRedoEntry: boolean;

  getName(): string {
    return 'UpdateMultipleElementsCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  private updates: Map<string, Partial<Element>>;
  // Deep snapshots of original elements to allow full restoration (type + properties) on undo
  private oldElementSnapshots: Map<string, Element> = new Map();
  private executed: boolean = false;

  constructor(
    editor: VideoEditor,
    updates: Map<string, Partial<Element>>,
    createUndoEntry: boolean = true,
  ) {
    this.editor = editor;
    this.updates = updates;
    this._shouldCreateUndoRedoEntry = createUndoEntry;

    this.storeOldProperties();
  }

  execute(): boolean {
    let success = true;
    for (const [elementId, newProps] of this.updates.entries()) {
      let performUpdate = () =>
        this.editor.timeline.updateElement(elementId, newProps);

      const element = this.editor.getElementById(elementId);
      if (element) {
        const transitionInfo = UpdateElementCommand.detectTypeTransition(
          element,
          newProps,
        );
        if (transitionInfo.needsTransition && transitionInfo.targetType) {
          const subCommand = new ElementTypeTransitionCommand(
            this.editor,
            elementId,
            transitionInfo.targetType,
            newProps,
          );
          performUpdate = () => subCommand.execute();
        }
      }

      const result = performUpdate();
      if (!result) {
        success = false;
      }
    }
    if (success) {
      this.executed = true;
      this.editor.markDirty('update_multiple_elements_command');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    }
    return success;
  }

  undo(): boolean {
    if (!this.executed) return false;
    let success = true;
    for (const [elementId, snapshot] of this.oldElementSnapshots.entries()) {
      const replaced = this.editor.timeline.replaceElement(
        elementId,
        snapshot.clone(), // clone again to avoid mutating stored snapshot across multiple undos/redos
      );
      if (!replaced) success = false;
    }
    if (success) {
      this.executed = false;
      this.editor.markDirty('update_multiple_elements_command_undo');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    }
    return success;
  }

  redo(): boolean {
    if (this.executed) return false;
    return this.execute();
  }

  getDescription(): string {
    return `Update multiple elements (${
      Array.from(this.updates.keys()).length
    })`;
  }

  private storeOldProperties() {
    for (const elementId of this.updates.keys()) {
      const element = this.editor.getElementById(elementId);
      if (element) {
        this.oldElementSnapshots.set(elementId, element.clone());
      }
    }
  }
}
